import * as React from 'react'
import { useMemo } from 'react'
import { Formik, Field } from 'formik'
import Link from 'next/link'
import { useTranslations } from 'next-intl'

import { Button, TextInputField } from './form'

import { colors, fonts } from '../utils/style'
import { createRegistrationFormValidation } from '../utils/validation'

import css from 'styled-jsx/css'

interface Props {
  onSubmit: (values: any, actions: any) => void
}

const { className: linkClassName, styles: linkStyles } = css.resolve`
  a {
    font-family: ${fonts.IBMPlex};
    font-size: 16px;
    line-height: 1;
    color: ${colors.grayText};
  }
`

export default function RegistrationForm({ onSubmit }: Props) {
  const t = useTranslations()
  const validationSchema = useMemo(() => createRegistrationFormValidation(t), [t])
  const initialValues = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    passwordConfirmation: '',
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formProps) => (
          <form onSubmit={formProps.handleSubmit} noValidate>
            <h1>{t('registrationPage.title')}</h1>

            <div className="field">
              <Field
                name="firstName"
                component={TextInputField}
                type="text"
                label={t('registrationPage.form.firstName')}
                autoTransliterate
              />
            </div>
            <div className="field">
              <Field
                name="lastName"
                component={TextInputField}
                type="text"
                label={t('registrationPage.form.lastName')}
                autoTransliterate
              />
            </div>
            <div className="field">
              <Field
                name="phone"
                component={TextInputField}
                type="text"
                label={t('registrationPage.form.phone')}
              />
            </div>
            <div className="field">
              <Field
                name="email"
                component={TextInputField}
                type="email"
                label={t('registrationPage.form.email')}
              />
            </div>
            <div className="field">
              <Field
                name="password"
                component={TextInputField}
                type="password"
                label={t('registrationPage.form.password')}
              />
            </div>
            <div className="field">
              <Field
                name="passwordConfirmation"
                component={TextInputField}
                type="password"
                label={t('registrationPage.form.passwordConfirmation')}
              />
            </div>

            <div className="actions">
              <div className="secondary-action">
                <div className="label">
                  {t('authSecondaryActions.alreadyRegistered')}
                </div>
                <Link href="/" className={linkClassName}>
                  {t('authSecondaryActions.login')}
                </Link>
              </div>
              <div className="primary-action">
                <Button
                  disabled={formProps.isSubmitting}
                  onClick={() => formProps.handleSubmit()}
                  type="submit"
                >
                  {formProps.isSubmitting
                    ? t('registrationPage.form.submitting')
                    : t('registrationPage.form.submit')}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
      <style jsx>{`
        form {
          width: 100%;
          max-width: 483px;
          margin: 0 auto;
          padding: 38px 53px 15px;
          border-radius: 6px;
          background: ${colors.white};
          box-shadow: 0px 10px 20px rgba(144, 144, 144, 0.1);
        }
        h1 {
          margin: 0 0 37px 0;
          font-family: ${fonts.IBMPlex};
          font-weight: bold;
          line-height: 58px;
          font-size: 50px;
          text-align: center;
          color: #14145a;
        }
        .field {
          margin-bottom: 20px;
        }
        .actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 26px;
        }
        .actions > * {
          flex: 1;
        }
        .secondary-action .label {
          margin-bottom: -3px;
          font-family: ${fonts.IBMPlex};
          font-size: 12px;
          line-height: 1;
          color: ${colors.inputText};
        }
      `}</style>

      {linkStyles}
    </React.Fragment>
  )
}
